import { useState, type FormEvent } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { BrandMark } from '../../components/patient-portal/BrandMark';
import { LanguageMenu } from '../../components/patient-portal/LanguageMenu';
import { Button } from '../../components/ui/Button';
import { Checkbox, Field, Input } from '../../components/ui/Field';
import { ErrorBanner } from '../../components/ui/States';
import { useI18n } from '../../i18n/I18nProvider';
import { useTheme } from '../../theme/ThemeProvider';
import { loginErrorMessage } from './loginError';

export type LoginSubmit = (values: { email: string; password: string; remember: boolean }) => Promise<void>;

/**
 * Credentials only — the caller owns the auth call, so this stays a form.
 */
export function LoginForm({ onSubmit }: { onSubmit: LoginSubmit }) {
  const { t } = useI18n();
  const { displayName } = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Editing anything is an attempt to fix the problem: retire the message.
  const clearError = () => setError((current) => (current === null ? current : null));

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({ email, password, remember });
    } catch (submitError) {
      setError(loginErrorMessage(submitError, t));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="flex flex-col justify-center p-6 sm:p-8 lg:p-12">
      <div className="w-full max-w-sm mx-auto">
        <BrandMark height={32} subtitle />

        {/* The hero is hidden on small screens, so the welcome comes here instead. */}
        <p className="text-body-sm text-ink-muted mt-4 lg:hidden">{t('auth.mobileWelcome')}</p>

        <h1 className="font-serif text-title text-ink-strong mt-8">{t('auth.heading')}</h1>
        <p className="text-body-sm text-ink-muted mt-1.5">{t('auth.subheading')}</p>

        <form className="mt-7 space-y-5" onSubmit={handleSubmit}>
          <Field label={t('auth.emailLabel')} htmlFor="auth-email">
            <Input
              id="auth-email"
              type="email"
              autoComplete="username"
              autoFocus
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
                clearError();
              }}
              placeholder={t('auth.emailPlaceholder')}
              required
            />
          </Field>

          <Field label={t('auth.passwordLabel')} htmlFor="auth-password">
            <span className="relative block">
              <Input
                id="auth-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                  clearError();
                }}
                placeholder={t('auth.passwordPlaceholder')}
                className="pe-11"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword((current) => !current)}
                aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                aria-pressed={showPassword}
                className="absolute top-1/2 -translate-y-1/2 end-1 flex h-9 w-9 items-center justify-center rounded-xs text-ink-faint hover:text-ink"
              >
                {showPassword ? <EyeOff size={17} aria-hidden="true" /> : <Eye size={17} aria-hidden="true" />}
              </button>
            </span>
          </Field>

          <Checkbox
            label={t('auth.rememberDevice')}
            checked={remember}
            onChange={() => setRemember((current) => !current)}
          />

          {error ? <ErrorBanner message={error} /> : null}

          <Button type="submit" block size="lg" loading={submitting}>
            {submitting ? t('auth.submitting') : t('auth.submit')}
          </Button>
        </form>

        <div className="mt-6 flex justify-center">
          <LanguageMenu compact={false} />
        </div>

        <hr className="border-0 border-t border-hairline my-7" />

        <p className="text-caption text-ink-muted text-start">
          {displayName ? t('auth.troubleClinic', { clinic: displayName }) : t('auth.troubleGeneric')}
        </p>
        <div className="mt-4 flex flex-wrap gap-x-5 gap-y-1 text-micro text-ink-faint">
          <span>{t('auth.privacyPolicy')}</span>
          <span>{t('auth.securityProtocol')}</span>
        </div>
      </div>
    </section>
  );
}
